// src/rms/agreements/resolve.js
// ============================================================================
// Turning a template body into the text that gets signed
// ============================================================================
// The other half of tokens.js. Split from it because the whitelist is pure data
// with no imports -- which is what lets the verifier load it under Node -- while
// resolution needs the operator, the customer, the asset, the booking and the
// app's own formatters.
//
// Substitution itself is messageService.fillTemplate. One implementation, not
// two: an agreement and a booking-confirmation email should never disagree
// about what {{starts_at}} means.
//
// Everything here runs ONCE, at the moment a document is sent or signed, and
// the result is frozen into rms_agreement_documents.body_snapshot. Nothing
// resolves a token at render time -- which is what stops the read-only page and
// the PDF from ever disagreeing, and what makes the stored hash worth having.
// ============================================================================

import { fillTemplate } from "../data/messageService";
import { formatDate, formatDateTime } from "../components/ui";
import { assetLabel } from "../utils/assetLabel";

const joined = (parts) =>
  parts.map((x) => String(x || "").trim()).filter(Boolean).join(", ");

/**
 * Resolve every token from what the app knows about one booking.
 *
 * A value this app does not have comes back as "" and is turned into a ruled
 * blank by renderBody. See the note there -- getting this wrong printed
 * "{{business_address}}" onto a contract at the counter.
 */
export function tokenValues({ operator, customer, asset, reservation, location, quote, formatMoney }) {
  const money = (cents) =>
    cents === null || cents === undefined || cents === ""
      ? ""
      : typeof formatMoney === "function"
        ? formatMoney(cents)
        : String((Number(cents) || 0) / 100);

  // business_address is free text with newlines preserved (20260819110000), and
  // these tokens land mid-sentence. Flatten it.
  const flat = (text) =>
    String(text || "").split("\n").map((line) => line.trim()).filter(Boolean).join(", ");

  const fullName = [customer?.first_name, customer?.last_name]
    .map((x) => String(x || "").trim())
    .filter(Boolean)
    .join(" ");

  return {
    business_name: operator?.business_name || "",
    business_address: flat(operator?.business_address),
    business_phone: operator?.business_phone || "",
    business_email: operator?.business_email || "",

    renter_name: fullName,
    renter_address: joined([
      customer?.address_line1,
      customer?.address_line2,
      customer?.city,
      customer?.region,
      customer?.postal_code,
      customer?.country,
    ]),
    renter_email: customer?.email || "",
    renter_phone: customer?.phone || "",
    licence_number: customer?.id_verification?.license_number || "",

    asset: asset ? assetLabel(asset) : "",
    asset_code: asset?.asset_code || "",

    reservation_number: reservation?.reservation_number || "",
    starts_at: reservation?.starts_at ? formatDateTime(reservation.starts_at) : "",
    ends_at: reservation?.ends_at ? formatDateTime(reservation.ends_at) : "",
    pickup_location: location?.name || "",

    // rate_snapshot is the quote taken at booking time, and check-in bills from
    // it rather than re-pricing. The agreement quotes the same figure for the
    // same reason: a rate change after the fact must not rewrite what the
    // customer agreed to.
    total: money(quote?.subtotal_cents ?? reservation?.quoted_total_cents),
    deposit: money(quote?.deposit_cents ?? asset?.deposit_cents),
    daily_rate: money(asset?.base_rate_cents),

    // rms_operators has no region column, only country. Most operators will
    // want their province or state here and are expected to type it into the
    // body directly; the token is a starting point, not the last word.
    governing_law: operator?.country || "",
    today: formatDate(new Date().toISOString()),
  };
}

/**
 * What an unfillable placeholder becomes on the page.
 *
 * A ruled blank, matching the ______________ blanks the starter templates
 * already use for things only the operator knows. On paper somebody writes in
 * it; on screen it reads as a form rather than as a bug.
 */
export const BLANK = "__________";

/**
 * Body in, finished prose out. The one place the two halves meet.
 *
 * ----------------------------------------------------------------------------
 * Why this is not just fillTemplate
 * ----------------------------------------------------------------------------
 * fillTemplate treats an empty value the same as an unknown one and leaves the
 * placeholder VISIBLE. That is right for an email -- "Your booking starts on "
 * with nothing after it reads as a broken sentence, while "{{starts_at}}" reads
 * as a template somebody has to fix.
 *
 * On a contract it is wrong twice over. A customer handed a page saying
 * "reached on {{business_phone}}" is being asked to sign something that looks
 * broken, and a court reading it later has to decide what the parties meant by
 * a curly brace. So a KNOWN token with no value becomes a blank, and only a
 * token this app has never heard of survives as itself -- which is a different
 * problem, belongs to the owner's template, and is what unknownTokens reports.
 */
export function renderBody(body, values) {
  const filled = {};
  for (const [key, value] of Object.entries(values || {})) {
    filled[key] = value === "" || value === null || value === undefined ? BLANK : value;
  }
  return fillTemplate(body, filled);
}

/**
 * Which known tokens this booking had nothing for.
 *
 * Computed from the VALUES rather than by scanning the rendered text, because
 * once renderBody has done its job a blank is indistinguishable from a blank
 * the operator typed themselves. The counter shows this so staff can see what
 * is missing before a customer signs a page full of ruled lines.
 */
export function blanksIn(body, values) {
  const text = String(body || "");
  return Object.keys(values || {}).filter(
    (key) => !values[key] && text.includes(`{{${key}}}`)
  );
}

export default { tokenValues, renderBody, blanksIn, BLANK };
